import React from "react";
import PropTypes from "prop-types";
import { Alert, FormGroup, Label } from "reactstrap";
import { useTranslation } from "react-i18next";
import { useAuthorizationContext } from "contexts/AuthorizationContext";
import SelectSearch from "components/SelectSearch";
import { searchOrganizations, sortByName } from "contexts/orgFilter";
import "./orgFilter.css";

// Hundreds of customers for a super admin: a page of matches at a time.
const firstMatches = (options) => (query = "") => searchOrganizations(options, query).items;

// In place of a page that works on one organization, when the filter does
// not name one where it can be used. `organizations` are only those where it
// can; choosing one sets the filter, and the page follows it. A modal that
// must not change the page behind it passes `onChoose` and keeps the choice
// to itself: the filter is left as it is. With `value` the organization is
// already chosen: it is shown selected, still changeable unless it is the
// only one, and the page's explanation is left out.
const ChooseOrganization = ({ organizations, inputId, onChoose = null, value = "" }) => {
  const { t } = useTranslation("common");
  const { filteredOrganization, setOrgFilter } = useAuthorizationContext();
  const options = sortByName(organizations).map((org) => ({
    label: org.name,
    value: org.guid,
    name: org.name,
  }));

  return (
    <div data-testid="choose-organization">
      {!value && (
        <Alert color="info" fade={false}>
          {filteredOrganization
            ? t("orgFilter.pageNotAvailableFor", { name: filteredOrganization.name })
            : t("orgFilter.pageWorksOnOneOrganization")}
        </Alert>
      )}
      {options.length === 0 ? (
        <Alert color="warning" fade={false}>
          {t("orgFilter.noOrganizationForPage")}
        </Alert>
      ) : (
        <FormGroup className="choose-organization__select">
          <Label for={inputId}>{t("fleet.organization")}</Label>
          <SelectSearch
            inputId={inputId}
            ariaLabel={t("fleet.organization")}
            filterOptions={firstMatches}
            options={options}
            name={inputId}
            placeholder={`${t("subscription.selectAnOrganization")}...`}
            search={true}
            value={value}
            disabled={!!value && options.length === 1}
            onChange={(guid) => guid && (onChoose ? onChoose(guid) : setOrgFilter(guid))}
            autoComplete="on"
          />
        </FormGroup>
      )}
    </div>
  );
};

ChooseOrganization.propTypes = {
  organizations: PropTypes.arrayOf(
    PropTypes.shape({ guid: PropTypes.string, name: PropTypes.string })
  ).isRequired,
  inputId: PropTypes.string.isRequired,
  onChoose: PropTypes.func,
  value: PropTypes.string,
};

export default ChooseOrganization;
